import clsx from "clsx";
import { CSSProperties, FC, useMemo } from "react";

import { ToolDropdownButton } from "@tsmono/react/components";
import { IconName } from "@tsmono/react/icons";
import { valueAsString } from "@tsmono/util";

import { ScoreValue } from "../../../@types/extraInspect";
import { ScoreLabel } from "../../../app/types";
import { BasicSampleData } from "../../../client/api/types";
import {
  kScoreTypeBoolean,
  kScoreTypeOther,
  kScoreTypePassFail,
} from "../../../constants";
import {
  resolveScorePanelSort,
  resolveScorePanelView,
  useEvalScorePanelSort,
  useEvalScorePanelView,
  useScorePanelSort,
  useScorePanelView,
} from "../../../state/hooks";
import {
  colorForValue,
  resolveScale,
} from "../../shared/samples-grid/colorScale";
import { EvalDescriptor } from "../descriptor/types";
import {
  useSamplesViewColorScalesEnabled,
  useSamplesViewScoreColorScales,
} from "../list/useSamplesView";

import styles from "./ScorePanel.module.css";
import { scoreTone, Tone } from "./scoreTone";
import { ScoreChipValueDisplay, ScoreValueDisplay } from "./ScoreValueDisplay";
import { ViewToggle } from "./ViewToggle";

interface ScorePanelProps {
  scores: ScoreLabel[];
  sample: BasicSampleData;
  evalDescriptor: EvalDescriptor;
}

interface RenderedScore {
  label: ScoreLabel;
  key: string;
  // null: dict-valued scores admit null entries (see scoreValue).
  value: ScoreValue | null | undefined;
  scoreType: string;
  tone: Tone;
  /** 0–1, only set for numeric values. Drives the chip's blue
   *  heatmap border so the highest displayed value reads darkest and
   *  the lowest reads lightest. */
  numericIntensity?: number;
  /** CSS color from `score_color_scales` — paints both the chip
   *  border and the inner mini-pill background. */
  bgColor?: string;
}

const TONE_RANK: Record<Tone, number> = {
  fail: 0,
  warn: 1,
  neutral: 2,
  pass: 3,
};

/**
 * Score panel for the V2 sample header — the right column when there
 * are 3+ scores. Defaults to chips for ≤ 6 scores, grid for 7+; the
 * user can flip via the icon-only segmented control. The panel header
 * also carries a sort dropdown that re-orders the scores in either
 * view (so chips and grid share the same sort UX).
 */
export const ScorePanel: FC<ScorePanelProps> = ({
  scores,
  sample,
  evalDescriptor,
}) => {
  const wireScoreColorScales = useSamplesViewScoreColorScales();
  const colorScalesEnabled = useSamplesViewColorScalesEnabled();

  const renderedScores = useMemo<RenderedScore[]>(() => {
    const initial = scores.map((label) => {
      const selected = evalDescriptor.score(sample, label);
      const descriptor = evalDescriptor.scoreDescriptor(label);
      const scoreType = descriptor?.scoreType ?? kScoreTypeOther;
      // Pass/fail and boolean already render via semantic tones; opting
      // them into a configured color scale would clash with the
      // chipFail / chipWarn border. Match the grid's exclusion at
      // columns.tsx:559-560.
      const acceptsColorScale =
        colorScalesEnabled &&
        scoreType !== kScoreTypePassFail &&
        scoreType !== kScoreTypeBoolean;
      let bgColor: string | undefined;
      if (acceptsColorScale && descriptor) {
        const wire = wireScoreColorScales[label.name];
        if (wire) {
          const resolved = resolveScale(wire, {
            min: descriptor.min,
            max: descriptor.max,
          });
          if (resolved) {
            bgColor = colorForValue(resolved, selected?.value);
          }
        }
      }
      return {
        label,
        key: `${label.scorer}__${label.name}`,
        value: selected?.value,
        scoreType,
        tone: scoreTone(selected?.value, scoreType),
        bgColor,
      };
    });

    // Heatmap intensity for numeric chips. Range comes from the actual
    // displayed numeric values so e.g. red-team flag scores (most are 1,
    // a few are 4-5) get visible contrast within the panel.
    const numbers = initial
      .map((s) => (typeof s.value === "number" ? s.value : null))
      .filter((v): v is number => v !== null);
    const min = numbers.length ? Math.min(...numbers) : 0;
    const max = numbers.length ? Math.max(...numbers) : 0;
    const range = max - min;
    return initial.map((s) => {
      if (typeof s.value !== "number") return s;
      const intensity =
        numbers.length <= 1 || range === 0 ? 1 : (s.value - min) / range;
      return { ...s, numericIntensity: intensity };
    });
  }, [
    scores,
    sample,
    evalDescriptor,
    wireScoreColorScales,
    colorScalesEnabled,
  ]);

  const count = renderedScores.length;
  const tight = count <= 6;
  // Cap the body at ~6 rows so the panel can't dominate the header.
  // Anything more scrolls inside the panel.
  const dense = count > 6;
  const evalView = useEvalScorePanelView();
  const evalSort = useEvalScorePanelSort();
  const [storedView, setView] = useScorePanelView();
  const view = resolveScorePanelView(storedView, evalView, count);

  const [storedSort, setSort] = useScorePanelSort();
  const sort = resolveScorePanelSort(storedSort, evalSort);
  const sortedScores = useMemo(() => {
    if (!sort.column) return renderedScores;
    const cmp = makeCompare(sort.column);
    const out = [...renderedScores].sort(cmp);
    if (sort.dir === "desc") out.reverse();
    return out;
  }, [renderedScores, sort.column, sort.dir]);

  const sortItems: Record<string, () => void> = {
    Default: () => setSort({ column: null, dir: "asc" }),
    "Score asc": () => setSort({ column: "value", dir: "asc" }),
    "Score desc": () => setSort({ column: "value", dir: "desc" }),
    "Scorer asc": () => setSort({ column: "name", dir: "asc" }),
    "Scorer desc": () => setSort({ column: "name", dir: "desc" }),
  };
  // Reflect the active sort direction in the trigger icon so users can
  // see at a glance whether the panel is currently sorted, and which
  // way. `arrow-down-up` for unsorted; up/down for asc/desc.
  const sortIcon: IconName = !sort.column
    ? "arrowDownUp"
    : sort.dir === "asc"
      ? "sortUp"
      : "sortDown";

  return (
    <div className={clsx(styles.panel, tight && styles.tight)}>
      <div className={styles.panelHeader}>
        <div className={styles.scoresHeader}>
          <span className={styles.columnLabel}>Scores</span>
          <span className={styles.scoresCount}>{count}</span>
        </div>
        <div className={styles.headerControls}>
          <div className={styles.sortWrapper}>
            <ToolDropdownButton
              label=""
              icon={sortIcon}
              subtle
              className={styles.sortButton}
              dropdownAlign="right"
              dropdownClassName="text-size-smallest"
              items={sortItems}
              aria-label="Sort scores"
              title="Sort scores"
            />
          </div>
          <ViewToggle view={view} setView={setView} />
        </div>
      </div>
      <div
        className={clsx(
          styles.panelBody,
          view === "grid" ? styles.bodyGrid : styles.bodyChips,
          dense && styles.dense
        )}
      >
        {view === "grid"
          ? sortedScores.map((s) => (
              <ScoreRow key={s.key} score={s} tight={tight} />
            ))
          : sortedScores.map((s) => <ScoreChip key={s.key} score={s} />)}
      </div>
    </div>
  );
};

interface ScoreRowProps {
  score: RenderedScore;
  tight: boolean;
}

const ScoreRow: FC<ScoreRowProps> = ({ score, tight }) => {
  return (
    <div
      className={clsx(styles.row, tight && styles.rowTight)}
      title={score.label.name}
    >
      <span className={styles.rowName}>{score.label.name}</span>
      <ScoreValueDisplay
        value={score.value}
        scoreType={score.scoreType}
        size={18}
      />
    </div>
  );
};

interface ScoreChipProps {
  score: RenderedScore;
}

const ScoreChip: FC<ScoreChipProps> = ({ score }) => {
  // Configured `score_color_scales` win: tint the chip border and the
  // inner mini-pill with the resolved (subtle) color. Otherwise
  // numeric neutral chips fall back to a blue border whose darkness
  // scales with the value relative to the other numeric values in the
  // panel. tone-attention chips (fail/warn) keep their semantic border.
  let style: CSSProperties | undefined;
  if (score.bgColor) {
    style = { borderColor: score.bgColor };
  } else if (score.tone === "neutral" && score.numericIntensity !== undefined) {
    const opacity = (0.25 + 0.75 * score.numericIntensity).toFixed(2);
    style = { borderColor: `rgba(var(--bs-primary-rgb), ${opacity})` };
  }
  return (
    <span
      className={clsx(
        styles.chip,
        score.tone === "fail" && styles.chipFail,
        score.tone === "warn" && styles.chipWarn
      )}
      style={style}
      title={score.label.name}
    >
      <ScoreChipValueDisplay
        value={score.value}
        scoreType={score.scoreType}
        size={16}
        bgColor={score.bgColor}
      />
      <span className={styles.chipName}>{score.label.name}</span>
    </span>
  );
};

function makeCompare(
  column: "name" | "value"
): (a: RenderedScore, b: RenderedScore) => number {
  if (column === "name") {
    return (a, b) =>
      a.label.name.localeCompare(b.label.name, undefined, {
        numeric: true,
        sensitivity: "base",
      });
  }
  return (a, b) => {
    const av = a.value;
    const bv = b.value;
    const an = typeof av === "number" ? av : null;
    const bn = typeof bv === "number" ? bv : null;
    if (an !== null && bn !== null) return an - bn;
    if (an !== null) return -1;
    if (bn !== null) return 1;
    const as = av === undefined ? "" : valueAsString(av);
    const bs = bv === undefined ? "" : valueAsString(bv);
    return as.localeCompare(bs, undefined, { numeric: true });
  };
}

// Re-export for callers that may want it later (e.g. an alternate
// "sort by tone status" entry point).
export { TONE_RANK };
